import React, { useEffect, useRef, useState } from "react";
import {
  useEditorState,
  type CircleFlowGroup,
  type CircleFlowSubcircle,
} from "@/store";
import { localizeCircleFlowText, postMessageToRef } from "@/utils";

// Matches .cfb-group-btn's transition-transform duration (350ms) in index.css.
const GROUP_EXPAND_ANIMATION_MS = 350;

export const groupLabel = (group: CircleFlowGroup) => {
  if (group.title) return group.title;
  if (group.name) return localizeCircleFlowText(group.name);
  return `${group.group_index}`;
};

export function CircleFlowBar() {
  const {
    circleFlow,
    godotContainerRef,
    circleFlowPendingIndex: pendingNavigateIndex,
    setCircleFlowPendingIndex: setPendingNavigateIndex,
    circleFlowGroupNavigatePending: groupNavigatePending,
    setCircleFlowGroupNavigatePending: setGroupNavigatePending,
  } = useEditorState();

  const activeGroup =
    circleFlow && circleFlow.length > 0
      ? (circleFlow.find((g) => g.active) ?? circleFlow[0])
      : null;

  // Locally expanded group, so clicking a group circle expands it immediately
  // instead of waiting for Godot to echo back an updated `active` flag.
  const [expandedGroupIndex, setExpandedGroupIndex] = useState<number | undefined>(
    activeGroup?.group_index,
  );

  const groupIndex = activeGroup?.group_index;

  useEffect(() => {
    if (groupIndex !== undefined) {
      setExpandedGroupIndex(groupIndex);
    }
  }, [groupIndex]);

  // Optimistic "this subcircle was just clicked" state, so the loading style
  // shows up immediately instead of depending on Godot's (sometimes flaky)
  // `loading` echo in setCircleFlow. Cleared once Godot confirms the target
  // subcircle actually became selected, or after a safety timeout. Lives in
  // the store (see circleFlowPendingIndex) so LayoutLoadingOverlay can share it.
  useEffect(() => {
    if (pendingNavigateIndex === null || !circleFlow) return;
    const resolved = circleFlow.some((group) =>
      group.subcircles.some((sub) => sub.index === pendingNavigateIndex && sub.selected),
    );
    if (resolved) {
      setPendingNavigateIndex(null);
    }
  }, [circleFlow, pendingNavigateIndex]);

  useEffect(() => {
    if (pendingNavigateIndex === null) return;
    const timeout = setTimeout(() => setPendingNavigateIndex(null), 30000);
    return () => clearTimeout(timeout);
  }, [pendingNavigateIndex]);

  // Holds off sending circleFlowNavigate for a group-circle click until the
  // group's own expand transition (.cfb-group-btn, duration-350) has played,
  // so the click doesn't visually skip straight to the loading state.
  const groupExpandTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (groupExpandTimeoutRef.current) clearTimeout(groupExpandTimeoutRef.current);
    };
  }, []);

  if (!circleFlow || circleFlow.length === 0 || !activeGroup) return null;

  // When there's only one group, the group number is meaningless, so we skip
  // rendering the group circle entirely and show bare sub-step nodes instead.
  const hasSingleGroup = circleFlow.length === 1;

  // Flat, ordered list of every subcircle across every group, for sequential prev/next navigation.
  const flatSubcircles = circleFlow.flatMap((group) =>
    group.subcircles.map((sub) => ({ sub, group })),
  );

  // `completed` and `visited` both mean "already passed through" for display purposes.
  const isPassed = (sub: CircleFlowSubcircle) => sub.visited || sub.completed;
  const currentFlatIndex = flatSubcircles.findIndex(({ sub }) => sub.selected);
  const selectedEntry =
    currentFlatIndex >= 0 ? flatSubcircles[currentFlatIndex] : null;

  const displayTitle = selectedEntry
    ? hasSingleGroup
      ? `${localizeCircleFlowText(selectedEntry?.sub?.title)}`
      : `${activeGroup.group_index}.${selectedEntry?.sub?.stage} ${localizeCircleFlowText(selectedEntry?.sub?.title)}`
    : "";

  const navigateTo = (sub: CircleFlowSubcircle) => {
    if (sub.locked || pendingNavigateIndex !== null) return;
    setPendingNavigateIndex(sub.index);
    requestAnimationFrame(() => {
      postMessageToRef(godotContainerRef, {
        action: "circleFlowNavigate",
        index: sub.index,
      });
    });
  };

  const handlePrev = () => {
    if (currentFlatIndex > 0) {
      const target = flatSubcircles[currentFlatIndex - 1];
      setExpandedGroupIndex(target.group.group_index);
      navigateTo(target.sub);
    }
  };

  const handleNext = () => {
    if (currentFlatIndex >= 0 && currentFlatIndex < flatSubcircles.length - 1) {
      const target = flatSubcircles[currentFlatIndex + 1];
      setExpandedGroupIndex(target.group.group_index);
      navigateTo(target.sub);
    }
  };

  const handleGroupClick = (group: CircleFlowGroup) => {
    if (pendingNavigateIndex !== null || groupNavigatePending) return;
    setExpandedGroupIndex(group.group_index);
    const target = group.subcircles[0];
    if (!target) return;
    setGroupNavigatePending(true);
    groupExpandTimeoutRef.current = setTimeout(() => {
      groupExpandTimeoutRef.current = null;
      setGroupNavigatePending(false);
      navigateTo(target);
    }, GROUP_EXPAND_ANIMATION_MS);
  };

  return (
    <div className="cfb-bar">

      {/* 1. Far Left Back Button */}
      <button
        onClick={handlePrev}
        disabled={currentFlatIndex <= 0 || pendingNavigateIndex !== null || groupNavigatePending}
        className="cfb-nav-btn"
        title="Previous Step"
      >
        <svg className="cfb-nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Centered Middle Section: Title + Vertical Line + Circles */}
      <div className="cfb-middle no-scrollbar">

        {/* Title Text */}
        <div className="cfb-title-wrap">
          <h1 className="cfb-title">
            {displayTitle}
          </h1>
        </div>

        {/* Thin Vertical Divider */}
        <div className="cfb-divider" />

        {/* Stepper Track Circles Area */}
        <div className="cfb-track">
          {circleFlow.map((group, index) => {
            const isExpanded = group.group_index === expandedGroupIndex;

            return (
              <div key={group.group_index} className="cfb-group">

                {hasSingleGroup ? (
                  /* Bare sub-step row: no group circle to show or expand into. */
                  <div className="cfb-substeps-inner">
                    {group.subcircles.length > 0 && !hasSingleGroup && (
                      <div className="cfb-substep-connector-start" />
                    )}

                    {group.subcircles.map((sub, subIdx) => (
                      <React.Fragment key={sub.index}>
                        {subIdx > 0 && (
                          <div className={`cfb-substep-connector ${isPassed(sub) ? 'cfb-substep-connector--visited' : 'cfb-substep-connector--pending'}`} />
                        )}

                        <SubStepNode
                          sub={sub}
                          group={group}
                          showGroupIndex={false}
                          pending={sub.index === pendingNavigateIndex}
                          navigationBlocked={pendingNavigateIndex !== null || groupNavigatePending}
                          onClick={() => navigateTo(sub)}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  /* Main Node */
                  <div className="cfb-group-node-wrap">
                    <button
                      onClick={() => handleGroupClick(group)}
                      disabled={pendingNavigateIndex !== null || groupNavigatePending}
                      className={`cfb-group-btn ${isExpanded ? 'cfb-group-btn--expanded' : 'cfb-group-btn--collapsed'}`}
                    >
                      {isExpanded ? (
                        <>
                          <svg className="cfb-progress-svg">
                            <circle className="cfb-progress-track-circle" cx="29" cy="29" r="25" />
                            <circle
                              className="progress-ring-circle"
                              cx="29"
                              cy="29"
                              r="25"
                              strokeDasharray="157"
                              strokeDashoffset={157 - (group.subcircles.reduce((acc, sub) => acc + (isPassed(sub) ? 1 : 0), 0) / group.subcircles.length) * 157}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="cfb-progress-badge">
                            {group.group_index}
                          </div>
                        </>
                      ) : (
                        <div className="cfb-group-circle-collapsed">
                          {group.group_index}
                        </div>
                      )}
                    </button>

                    {/* Expandable Sub-steps Pill Container */}
                    <div
                      className={`cfb-substeps-grid ${isExpanded ? 'cfb-substeps-grid--expanded' : 'cfb-substeps-grid--collapsed'} ${pendingNavigateIndex !== null ? 'cfb-transition-none' : ''}`}
                    >
                      <div
                        className={`cfb-substeps-pill ${isExpanded ? 'cfb-substeps-pill--expanded' : 'cfb-substeps-pill--collapsed'} ${pendingNavigateIndex !== null ? 'cfb-transition-none' : ''}`}
                      >
                        <div className="cfb-substeps-inner">
                          {group.subcircles.length > 0 && (
                            <div className="cfb-substep-connector-start" />
                          )}

                          {group.subcircles.map((sub, subIdx) => (
                            <React.Fragment key={sub.index}>
                              {subIdx > 0 && (
                                <div className={`cfb-substep-connector ${isPassed(sub) ? 'cfb-substep-connector--visited' : 'cfb-substep-connector--pending'}`} />
                              )}

                              <SubStepNode
                                sub={sub}
                                group={group}
                                showGroupIndex={true}
                                pending={sub.index === pendingNavigateIndex}
                                navigationBlocked={pendingNavigateIndex !== null || groupNavigatePending}
                                onClick={() => navigateTo(sub)}
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Connecting line between main group circles */}
                {index < circleFlow.length - 1 && (
                  <div
                    className={`cfb-group-connector ${isExpanded ? 'cfb-group-connector--expanded' : 'cfb-group-connector--collapsed'} ${pendingNavigateIndex !== null ? 'cfb-transition-none' : ''}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. Far Right Forward Button */}
      <button
        onClick={handleNext}
        disabled={currentFlatIndex < 0 || currentFlatIndex >= flatSubcircles.length - 1 || pendingNavigateIndex !== null || groupNavigatePending}
        className="cfb-nav-btn"
        title="Next Step"
      >
        <svg className="cfb-nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

    </div>
  );
}

/* Sub-step Node Component */
function SubStepNode({
  sub,
  group,
  showGroupIndex,
  pending,
  navigationBlocked,
  onClick,
}: {
  sub: CircleFlowSubcircle;
  group: CircleFlowGroup;
  showGroupIndex: boolean;
  pending: boolean;
  navigationBlocked: boolean;
  onClick: () => void;
}) {
  const { achievements, parseAchievementId } = useEditorState();

  const icon = sub.icon?.toLowerCase();
  const isDiamond = icon === "code";
  const isSquare = icon === "qa" || icon === "q&a";
  const isLocked = sub.locked;
  const isLoading = sub.loading || pending;

  let stateClass = "sub-step-node";

  if (isLocked) {
    stateClass = "sub-step-node-locked";
  } else if (sub.selected) {
    stateClass = "sub-step-node-selected";
  } else if (sub.completed || sub.visited) {
    stateClass = "sub-step-node-visited";
  }

  // Checkpoint outline: grey if the checkpoint isn't achieved yet, bright
  // green once it is. Only shown for non-locked subcircles that actually
  // carry a checkpoint.
  const hasCheckpoint = !isLocked && sub.checkpoint_id !== null && sub.checkpoint_id !== undefined;
  const checkpointAchieved =
    hasCheckpoint &&
    (achievements?.some(
      (achievement) =>
        String(achievement.id) === String(parseAchievementId(sub.checkpoint_id)) && achievement.is_achieved,
    ) ?? false);
  const checkpointBorderClass = hasCheckpoint
    ? checkpointAchieved
      ? "sub-step-node-checkpoint--achieved"
      : "sub-step-node-checkpoint--pending"
    : "";

  const shapeClass = isDiamond
    ? "diamond sub-step-node-shape--diamond"
    : isSquare
      ? "sub-step-node-shape--square"
      : "sub-step-node-shape--circle";

  return (
    <div className="group sub-step-wrap">
      {/* Floating Locked Tooltip Indicator */}
      {isLocked && (
        <div className="sub-step-tooltip">
          <span>🔒</span> Locked
        </div>
      )}

      <button
        onClick={onClick}
        disabled={isLocked || navigationBlocked}
        className={`sub-step-btn ${sub.selected ? 'sub-step-btn--selected' : ''}`}
      >
        <div
          className={`sub-step-node-shape ${shapeClass}`}
        >
          <div className={`sub-step-node-base ${stateClass} ${checkpointBorderClass}`}>
            <span className={`sub-step-node-title ${isDiamond ? 'diamond-text' : ''}`}>
              {showGroupIndex ? <>{group.group_index}.{sub.stage}</> : sub.stage}
            </span>
          </div>

          {/* Loading overlay: always mounted, opacity-only crossfade so it
              keeps showing/hiding reliably even if the main thread stalls
              during a Godot scene transition (see sub-step-fill-empty). It's
              a sibling of the base layer above (not just an inset-0 child of
              the bordered element) so its border fully covers the base
              layer's border instead of both showing at once. */}
          <div
            className={`sub-step-node-loading-overlay ${isLoading ? 'sub-step-node-loading-overlay--active' : ''}`}
          >
            <span className={`sub-step-node-title ${isDiamond ? 'diamond-text' : ''}`}>
              {showGroupIndex ? <>{group.group_index}.{sub.stage}</> : sub.stage}
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}
